import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth/authService';
import {
  Box,
  Paper,
  Button,
  Typography,
  Container,
  Avatar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  VerifiedUser,
  Error,
  Email,
  CheckCircle
} from '@mui/icons-material';

const EmailVerificationPage = () => {
  const [verificationState, setVerificationState] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Verify email token when component mounts
  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setVerificationState('error');
        setErrorMessage('Invalid or missing verification token.');
        return;
      }

      try {
        setVerificationState('verifying');
        await authService.verifyEmail(token);
        setVerificationState('success');
      } catch (error) {
        console.error('Email verification failed:', error);
        setVerificationState('error');
        
        if (error.status === 400 || error.message.includes('token')) {
          setErrorMessage('The verification link has expired or is invalid.');
        } else if (error.message.includes('already verified')) {
          setErrorMessage('This email address has already been verified.');
        } else {
          setErrorMessage(error.message || 'Email verification failed. Please try again.');
        }
      }
    };

    verifyEmail();
  }, [token]);

  const handleResendVerification = async () => {
    // This would need the email address - for now, redirect to forgot password
    // In a real implementation, you might store the email in session storage
    // or have a separate resend verification endpoint
    navigate('/forgot-password');
  };

  // Verifying state
  if (verificationState === 'verifying') {
    return (
      <Container 
        component="main" 
        maxWidth="sm"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper 
          elevation={6}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Avatar 
            sx={{ 
              m: 1, 
              bgcolor: 'primary.main',
              width: 56,
              height: 56
            }}
          >
            <Email />
          </Avatar>
          
          <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
            Verifying Email...
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Please wait while we verify your email address.
          </Typography>

          <CircularProgress size={40} sx={{ mb: 3 }} />

          <Typography variant="body2" color="text.secondary">
            This may take a few moments.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Success state
  if (verificationState === 'success') {
    return (
      <Container 
        component="main" 
        maxWidth="sm"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper 
          elevation={6}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Avatar 
            sx={{ 
              m: 1, 
              bgcolor: 'success.main',
              width: 56,
              height: 56
            }}
          >
            <CheckCircle />
          </Avatar>
          
          <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
            Email Verified!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your email address has been successfully verified. You can now sign in to your account.
          </Typography>

          <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
            Welcome to GeoAI-RR! Your Manager account is now active and ready to use.
          </Alert>

          <Button
            component={Link}
            to="/login"
            variant="contained"
            fullWidth
            sx={{ py: 1.5 }}
          >
            Sign In to Your Account
          </Button>
        </Paper>
      </Container>
    );
  }

  // Error state
  return (
    <Container 
      component="main" 
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Paper 
        elevation={6}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <Avatar 
          sx={{ 
            m: 1, 
            bgcolor: 'error.main',
            width: 56,
            height: 56
          }}
        >
          <Error />
        </Avatar>
        
        <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
          Verification Failed
        </Typography>
        
        <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
          {errorMessage}
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          If you're having trouble verifying your email, you can request a new verification link or contact support.
        </Typography>

        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleResendVerification}
            disabled={isLoading}
            sx={{ py: 1.5 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Requesting...
              </>
            ) : (
              'Request New Verification Link'
            )}
          </Button>
          
          <Button
            component={Link}
            to="/login"
            variant="outlined"
            fullWidth
            sx={{ py: 1.5 }}
          >
            Back to Sign In
          </Button>
          
          <Button
            component={Link}
            to="/register"
            variant="text"
            fullWidth
            sx={{ py: 1.5 }}
          >
            Create New Account
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default EmailVerificationPage; 